import {
  ROTULO_DO_ESTADO,
  STATUS_ESCOLHIVEIS,
  agruparPorEstado,
  moduleAccessKey,
  type EstadoPresenca,
  type Plataforma,
  type MembroPresenca,
  type PresencaDeMembro,
  type StatusEscolhido,
} from '@orbind/core'
import { AnchoredOverlay } from '@orbind/ui'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { api, apiOrigin } from '../../lib/api.ts'
import { usePermission } from '../../lib/access-control.ts'
import { usePresence } from './PresenceProvider.tsx'
import './members-panel.css'

/**
 * Painel lateral de membros (SPEC-20260912-0145).
 *
 * NAO e uma lista de conectados: e a lista de TODAS as pessoas do workspace,
 * com a conexao marcada em cima dela. Quem nunca conectou aparece.
 */

/** Foto enviada vence a url externa — mesma regra do `sessionAvatarSrc`. */
function avatarSrc(membro: MembroPresenca): string | null {
  if (membro.hasAvatar) {
    return `${apiOrigin()}/public/member-avatar/${membro.id}?v=${encodeURIComponent(membro.avatarVersion ?? '')}`
  }
  return membro.avatarUrl
}

/** Banner do perfil — mesma regra do avatar (rota pública + versão no `?v=`). */
function bannerSrc(membro: MembroPresenca): string | null {
  if (!membro.hasBanner) return null
  return `${apiOrigin()}/public/member-banner/${membro.id}?v=${encodeURIComponent(membro.avatarVersion ?? '')}`
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/)
  const primeira = partes[0]?.[0] ?? ''
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : ''
  return (primeira + ultima).toUpperCase()
}

function Avatar({ membro }: { membro: MembroPresenca }) {
  const src = avatarSrc(membro)
  if (src) return <img className="mpanel__avatar-img" src={src} alt="" />
  return (
    <span className="mpanel__avatar-iniciais" aria-hidden>
      {iniciais(membro.name)}
    </span>
  )
}

type Alvo = { membro: MembroPresenca; elemento: HTMLElement }

/**
 * O pontinho de estado.
 *
 * NUNCA sozinho: toda vez que ele aparece, o estado tambem esta escrito por
 * perto (em texto visivel no perfil, ou para leitor de tela na lista). Status
 * comunicado so por cor esta fora do padrao do projeto.
 */
/**
 * Por onde a pessoa está conectada (SPEC-20260924-0924).
 *
 * Saber se alguém está no computador ou só no celular muda o que se espera
 * dela: quem está no celular responde curto e demora mais a abrir um arquivo.
 *
 * Ícone COM texto para leitor de tela — a informação não pode existir só como
 * desenho, e "desktop" e "navegador" são parecidos demais para quem enxerga
 * pouco distinguir por forma.
 */
const ICONE_DA_PLATAFORMA: Record<Plataforma, { icone: string; nome: string }> = {
  desktop: { icone: 'fas fa-desktop', nome: 'aplicativo no computador' },
  navegador: { icone: 'fas fa-globe', nome: 'navegador' },
  mobile: { icone: 'fas fa-mobile-alt', nome: 'celular' },
}

function SinaisDePlataforma({ plataformas }: { plataformas: readonly Plataforma[] }) {
  if (plataformas.length === 0) return null
  return (
    <span className="mpanel__plataformas">
      {plataformas.map((p) => (
        <span key={p} className="mpanel__plataforma" title={`Conectada pelo ${ICONE_DA_PLATAFORMA[p].nome}`}>
          <i className={ICONE_DA_PLATAFORMA[p].icone} aria-hidden />
          <span className="ob-sr-only">{ICONE_DA_PLATAFORMA[p].nome}</span>
        </span>
      ))}
    </span>
  )
}

function PontoDeEstado({ estado, badge = false }: { estado: EstadoPresenca; badge?: boolean }) {
  return (
    <span
      className={`mpanel__ponto mpanel__ponto--${estado}${badge ? ' mpanel__ponto--badge' : ''}`}
      aria-hidden
    />
  )
}

/** Cartao de perfil, ancorado na linha que o abriu. */
function CartaoDePerfil({
  alvo,
  presenca,
  onClose,
}: {
  alvo: Alvo
  presenca: PresencaDeMembro | null
  onClose: () => void
}) {
  const estado = presenca?.estado ?? 'desconectado'
  const banner = bannerSrc(alvo.membro)
  return (
    <AnchoredOverlay
      anchor={alvo.elemento}
      onClose={onClose}
      prefer="esquerda"
      role="dialog"
      aria-label={`Perfil de ${alvo.membro.name}`}
      className="mpanel__perfil"
    >
      {/* Banner atrás da foto — o perfil personalizado da pessoa (2026-09-16).
          Sem banner, fica a faixa lisa do tema, e o cartão não muda de forma. */}
      <div className="mpanel__perfil-capa" data-ob-ampliar>
        {banner ? <img className="mpanel__perfil-banner" src={banner} alt={`Banner de ${alvo.membro.name}`} /> : null}
      </div>
      <div className="mpanel__perfil-topo">
        {/* `data-ob-ampliar`: clicar na foto abre ela grande no meio da tela. */}
        <span className="mpanel__avatar mpanel__avatar--grande mpanel__perfil-foto" data-ob-ampliar>
          <Avatar membro={alvo.membro} />
        </span>
        <div className="mpanel__perfil-id">
          <strong className="mpanel__perfil-nome">{alvo.membro.name}</strong>
          <span className="mpanel__perfil-mail">{alvo.membro.email}</span>
        </div>
      </div>
      {alvo.membro.bio ? <p className="mpanel__perfil-sobre">{alvo.membro.bio}</p> : null}
      {/*
        O estado de conexao vem da MESMA fonte da lista. Recalcular aqui por
        outro caminho criaria duas verdades sobre a mesma pessoa na tela.
        Nunca so por cor: o texto diz o estado.
      */}
      <div className="mpanel__perfil-estado">
        <PontoDeEstado estado={estado} />
        <span>{ROTULO_DO_ESTADO[estado]}</span>
      </div>
      {!alvo.membro.isActive ? <div className="mpanel__perfil-inativo">Cadastro inativo</div> : null}
    </AnchoredOverlay>
  )
}

/** Menu da acao secundaria (clique direito). */
function MenuDeAcoes({ alvo, onClose, onVerPerfil }: { alvo: Alvo; onClose: () => void; onVerPerfil: () => void }) {
  // Copiar contato de colega e dado administrado em Administracao > Membros:
  // quem nao administra ve a acao APAGADA, nunca escondida — sumir faria
  // parecer que o produto nao tem o recurso.
  const podeCopiar = usePermission(moduleAccessKey('admin'))

  return (
    <AnchoredOverlay
      anchor={alvo.elemento}
      onClose={onClose}
      prefer="esquerda"
      role="menu"
      aria-label={`Ações de ${alvo.membro.name}`}
      className="mpanel__menu"
    >
      <button type="button" className="mpanel__menu-item" role="menuitem" onClick={onVerPerfil}>
        <i className="fas fa-id-card" aria-hidden />
        Ver perfil
      </button>
      <button
        type="button"
        className="mpanel__menu-item"
        role="menuitem"
        disabled={!podeCopiar}
        title={podeCopiar ? undefined : 'Você não tem permissão para copiar o contato'}
        onClick={() => {
          void navigator.clipboard?.writeText(alvo.membro.email)
          onClose()
        }}
      >
        <i className="fas fa-envelope" aria-hidden />
        Copiar e-mail
      </button>
    </AnchoredOverlay>
  )
}

function Grupo({
  titulo,
  membros,
  presencas,
  eu,
  onAbrirPerfil,
  onAbrirMenu,
  onAbrirChat,
}: {
  titulo: string
  membros: MembroPresenca[]
  presencas: ReadonlyMap<string, PresencaDeMembro>
  /** Id de quem está logado: a própria linha não ganha o ícone de chat. */
  eu: string | null
  onAbrirPerfil: (alvo: Alvo) => void
  onAbrirMenu: (alvo: Alvo) => void
  /** Ícone de chat à direita do nome (SPEC-20260916-1455). */
  onAbrirChat: (alvo: Alvo) => void
}) {
  // Grupo vazio nao desenha NADA — nem cabecalho, nem contagem zerada.
  if (membros.length === 0) return null

  return (
    <section className="mpanel__grupo">
      <h3 className="mpanel__grupo-head">
        <span className="mpanel__grupo-titulo">{titulo}</span>
        <span className="mpanel__grupo-contagem">{membros.length}</span>
      </h3>
      <ul className="mpanel__lista" role="list">
        {membros.map((membro) => {
          const presenca = presencas.get(membro.id) ?? null
          const estado = presenca?.estado ?? 'desconectado'
          // "no computador" fica no grupo dos disponíveis (agruparPorEstado): não esmaece
          const online = estado === 'disponivel' || estado === 'ausente' || estado === 'no-computador'
          return (
            <li key={membro.id}>
              {/*
                Imagem E nome sao o MESMO alvo: por muito tempo so a imagem
                abria o perfil, e quem mirava no nome — o alvo maior, e o que a
                pessoa le — nao conseguia nada.
              */}
              <button
                type="button"
                className={`mpanel__linha${online ? '' : ' mpanel__linha--off'}`}
                title={`Conversar com ${membro.name}`}
                onClick={(evento) => onAbrirPerfil({ membro, elemento: evento.currentTarget })}
                onContextMenu={(evento) => {
                  evento.preventDefault()
                  onAbrirMenu({ membro, elemento: evento.currentTarget })
                }}
              >
                <span className="mpanel__avatar">
                  <Avatar membro={membro} />
                  <PontoDeEstado estado={estado} badge />
                </span>
                <span className="mpanel__identidade">
                  <span className="mpanel__nome">{membro.name}</span>
                  {/* o programa em uso no computador, bem pequeno
                      (SPEC-20261020-2341-programa-em-uso-na-presenca); o servidor só manda de quem
                      está "no computador" e não se declarou ausente nem offline */}
                  {presenca?.programa ? (
                    <span className="mpanel__programa" title={`Usando ${presenca.programa} agora`}>
                      {presenca.programa}
                    </span>
                  ) : null}
                </span>
                {/* Declarou-se ausente, mas está no computador: as duas coisas
                    aparecem (pedido 2026-09-24). Quem vai chamar quer saber
                    que ela pediu para não ser incomodada E que está por lá. */}
                {presenca?.declarado === 'ausente' ? (
                  <span className="mpanel__selo" title="Ela mesma se marcou como ausente">
                    ausente
                  </span>
                ) : null}
                <SinaisDePlataforma plataformas={presenca?.plataformas ?? []} />
                {/* O estado tambem em TEXTO, nunca so pela cor do ponto. */}
                <span className="ob-sr-only">
                  {ROTULO_DO_ESTADO[estado]}
                  {presenca?.declarado === 'ausente' ? ', marcada como ausente por ela mesma' : ''}
                </span>
              </button>
              {/* ÍCONE DE CHAT em cada nome (SPEC-20260916-1455, pedido
                  2026-09-16 14:55): irmão do botão da linha (botão dentro de
                  botão não vale), posicionado à direita pelo CSS */}
              {membro.id !== eu && membro.isActive ? (
                <button
                  type="button"
                  className="mpanel__chat"
                  aria-label={`Conversar com ${membro.name}`}
                  title={`Conversar com ${membro.name}`}
                  onClick={(evento) => onAbrirChat({ membro, elemento: evento.currentTarget })}
                >
                  <i className="fas fa-comment" aria-hidden />
                </button>
              ) : null}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** Limites do arraste: estreito demais corta o nome, largo demais come a tela. */
const LARGURA_MIN = 200
const LARGURA_MAX = 520

export function MembersPanel({
  aberto,
  largura,
  onAlternar,
  onLargura,
}: {
  aberto: boolean
  /** Largura escolhida pela pessoa (vem da conta, ver usePreferenciaDeLayout). */
  largura: number
  onAlternar: () => void
  onLargura: (valor: number) => void
}) {
  const { membros, presencas, eu, carregando } = usePresence()
  const [perfil, setPerfil] = useState<Alvo | null>(null)
  const [menu, setMenu] = useState<Alvo | null>(null)
  const navigate = useNavigate()

  /**
   * CLIQUE NA PESSOA ABRE O CHAT com ela (SPEC-20260916-1238, pedido
   * 2026-09-16 12:34: "ao clicar sobre um usuário ativo na barra de status deve
   * abrir o chat com esse usuário"). A conversa direta é aberta (ou achada)
   * pela API e a tela vai para ela. Na própria linha, ou com cadastro inativo,
   * continua abrindo o perfil; o perfil dos outros fica no menu do botão direito.
   */
  const abrirChat = async (alvo: Alvo) => {
    if (alvo.membro.id === eu || !alvo.membro.isActive) {
      setPerfil(alvo)
      return
    }
    try {
      const resposta = await api.chat.channels.direct.$post({ json: { memberId: alvo.membro.id } })
      if (!resposta.ok) throw new Error()
      const { channelId } = (await resposta.json()) as { channelId: string }
      navigate(`/channels/${channelId}`)
    } catch {
      // sem conversa possível (contato de fora, chat indisponível): mostra o perfil
      setPerfil(alvo)
    }
  }
  /** Busca: com muita gente conectada, rolar a lista deixa de servir. */
  const [busca, setBusca] = useState('')

  /**
   * Acoes sobre si mesmo nao entram no menu (secao 8 do contrato). Como as
   * duas acoes existentes sao sobre a pessoa alvo, o menu inteiro nao abre na
   * propria linha — abrir um menu vazio seria pior que nao abrir.
   */
  const abrirMenu = (alvo: Alvo) => {
    if (alvo.membro.id === eu) return
    setMenu(alvo)
  }

  /**
   * Filtra por nome ou e-mail, ignorando acento e caixa — quem procura "jose"
   * precisa achar "José". Derivado a cada render, nunca guardado.
   */
  const semAcento = (t: string) =>
    t.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
  const termo = semAcento(busca.trim())
  const visiveis = termo === ''
    ? membros
    : membros.filter((m) => semAcento(m.name).includes(termo) || semAcento(m.email).includes(termo))
  const grupos = agruparPorEstado(visiveis, presencas)

  /**
   * Arraste da borda ESQUERDA: o painel cresce para a esquerda, entao a largura
   * e a distancia do ponteiro ate a borda direita da janela. Enquanto arrasta,
   * a transicao sai do caminho — ela existe para o abrir/fechar e faria a
   * largura "perseguir" o ponteiro com atraso.
   */
  const arrastando = useRef(false)
  const iniciarArraste = (e: React.MouseEvent) => {
    e.preventDefault()
    arrastando.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    const mover = (ev: MouseEvent) => {
      if (!arrastando.current) return
      onLargura(Math.round(Math.min(LARGURA_MAX, Math.max(LARGURA_MIN, window.innerWidth - ev.clientX))))
    }
    const soltar = () => {
      arrastando.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', mover)
      document.removeEventListener('mouseup', soltar)
    }
    document.addEventListener('mousemove', mover)
    document.addEventListener('mouseup', soltar)
  }

  return (
    <aside
      className={`mpanel${aberto ? '' : ' mpanel--fechado'}${arrastando.current ? ' mpanel--arrastando' : ''}`}
      style={{ width: largura }}
      aria-label="Membros"
    >
      {aberto ? (
        <span
          className="mpanel__alca"
          role="separator"
          aria-orientation="vertical"
          aria-label="Ajustar a largura do painel"
          title="Arraste para ajustar a largura"
          onMouseDown={iniciarArraste}
        />
      ) : null}
      <header className="mpanel__head">
        {/*
          O botao e o MESMO elemento nos dois estados — nunca desmontado. E o
          que faz o icone viajar junto com a coluna ao abrir e fechar, em vez
          de um botao sumir e outro aparecer no canto.
        */}
        <button
          type="button"
          className="mpanel__alternar"
          aria-label={aberto ? 'Fechar painel de membros' : 'Abrir painel de membros'}
          aria-expanded={aberto}
          title={aberto ? 'Fechar painel de membros' : 'Membros'}
          onClick={onAlternar}
        >
          <i className="fas fa-user-friends" aria-hidden />
        </button>
        <h2 className="mpanel__titulo">Membros</h2>
      </header>
      {/* Fechada, a coluna some do fluxo de leitura: nada aqui e alcancavel
          por Tab nem anunciado por leitor de tela. */}
      <div className="mpanel__corpo" hidden={!aberto}>
        {/* Busca acima da lista: some junto com ela quando não há ninguém. */}
        {!carregando && membros.length > 0 ? (
          <div className="mpanel__busca">
            <i className="fas fa-search" aria-hidden />
            <input
              type="search"
              value={busca}
              placeholder="Buscar pessoa…"
              aria-label="Buscar pessoa pelo nome ou e-mail"
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setBusca('')
              }}
            />
            {busca ? (
              <button type="button" aria-label="Limpar a busca" title="Limpar" onClick={() => setBusca('')}>
                <i className="fas fa-times" aria-hidden />
              </button>
            ) : null}
          </div>
        ) : null}
        {carregando ? (
          <p className="mpanel__vazio">Carregando membros…</p>
        ) : membros.length === 0 ? (
          <p className="mpanel__vazio">Nenhum membro neste workspace.</p>
        ) : visiveis.length === 0 ? (
          <p className="mpanel__vazio">Ninguém encontrado para “{busca.trim()}”.</p>
        ) : (
          <>
            <Grupo
              titulo="Conectados"
              membros={grupos.disponiveis}
              presencas={presencas}
              eu={eu}
              onAbrirPerfil={(alvo) => void abrirChat(alvo)}
              onAbrirMenu={abrirMenu}
              onAbrirChat={(alvo) => void abrirChat(alvo)}
            />
            {/* Ausente tem grupo proprio: a categoria diz o estado, sem
                precisar de marcacao extra na linha (pedido do usuario). */}
            <Grupo
              titulo="Ausentes"
              membros={grupos.ausentes}
              presencas={presencas}
              eu={eu}
              onAbrirPerfil={(alvo) => void abrirChat(alvo)}
              onAbrirMenu={abrirMenu}
              onAbrirChat={(alvo) => void abrirChat(alvo)}
            />
            <Grupo
              titulo="Não conectados"
              membros={grupos.desconectados}
              presencas={presencas}
              eu={eu}
              onAbrirPerfil={(alvo) => void abrirChat(alvo)}
              onAbrirMenu={abrirMenu}
              onAbrirChat={(alvo) => void abrirChat(alvo)}
            />
          </>
        )}
      </div>

      {perfil ? (
        <CartaoDePerfil
          alvo={perfil}
          presenca={presencas.get(perfil.membro.id) ?? null}
          onClose={() => setPerfil(null)}
        />
      ) : null}
      {menu ? (
        <MenuDeAcoes
          alvo={menu}
          onClose={() => setMenu(null)}
          onVerPerfil={() => {
            setPerfil(menu)
            setMenu(null)
          }}
        />
      ) : null}
    </aside>
  )
}

/**
 * Seletor de status do menu do perfil.
 *
 * Mostra o estado EFETIVO (inclui o "ausente" automatico), mas o check marca a
 * ESCOLHA — quem esta ausente por ociosidade continua com "Disponível"
 * marcado, porque foi isso que escolheu. Confundir os dois faria a pessoa achar
 * que o sistema mudou a escolha dela sozinho.
 */
export function SeletorDeStatus({ onEscolher }: { onEscolher: () => void }) {
  const { meuEstado, escolherStatus } = usePresence()
  // Ausente automatico nao mexe na escolha: a escolha por tras dele e sempre
  // "disponivel", senao a pessoa voltaria do almoco marcada como ausente.
  const escolhaAtual: StatusEscolhido =
    meuEstado === 'desconectado' || meuEstado === 'no-computador' ? 'disponivel' : meuEstado

  return (
    <div className="statusmenu" role="group" aria-label="Meu status">
      <span className="statusmenu__rotulo">Status</span>
      {STATUS_ESCOLHIVEIS.map((status) => {
        const atual = status === escolhaAtual
        return (
          <button
            key={status}
            type="button"
            role="menuitemradio"
            aria-checked={atual}
            className="statusmenu__item"
            onClick={() => {
              escolherStatus(status)
              onEscolher()
            }}
          >
            <PontoDeEstado estado={status} />
            {ROTULO_DO_ESTADO[status]}
            {atual ? <i className="fas fa-check statusmenu__check" aria-hidden /> : null}
          </button>
        )
      })}
    </div>
  )
}
